// External imports
import React, { useState, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  IconButton,
  Tooltip,
  Container,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Internal imports
import { useStoreByName } from "../../../hooks/use-stores.hooks";
import { AddStudentsToClassRequest, Class, ImportClassResponse, Student, FailedUser } from "@shared/types";
import FailedUsersModal from "../modals/FailedUsersModal";
import { LoaderComponent } from "../core/LoaderComponent";
import IconImportTable from "../../../assets/SVGComponents/IconImportTable";
import ImportExcelModal from '../modals/ImportExcelModal';

// Assets
import AddUserIcon from '../../../assets/icons/iconAddUser.svg';
import OpenDoorIcon from '../../../assets/icons/iconDoorOpen.svg';
import CloseDoorIcon from '../../../assets/icons/iconDoorClose.svg';
import { observer } from "mobx-react-lite";

// Types
interface StudentsTableProps {
  classItem: Class;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: string;
  label: string | React.ReactNode;
  sortable: boolean;
  width: string;
}

/**
 * StudentsTable Component
 * Displays a table of students with sorting, adding, and importing capabilities
 */
const StudentsTable = observer(({ classItem }: StudentsTableProps) => {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teacherStore = useStoreByName("teacherStore");

  // Table State
  const [orderBy, setOrderBy] = useState<keyof Student>('name');
  const [order, setOrder] = useState<Order>('asc');
  const [hoveredStudentId, setHoveredStudentId] = useState<number | null>(null);
  
  // Add Student State
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: '', email: '' });
  const [isSaving, setIsSaving] = useState(false);
  
  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [showFailedUsersModal, setShowFailedUsersModal] = useState(false);
  const [failedUsers, setFailedUsers] = useState<FailedUser[]>([]);

  const addStudentRowRef = useRef<HTMLTableRowElement | null>(null);

  const scrollToAddStudentRow = () => {
    if (addStudentRowRef.current) {
      addStudentRowRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Table Configuration
  const headCells: HeadCell[] = [
    {
      id: 'index',
      label: '#',
      sortable: false,
      width: '5%'
    },
    {
      id: 'name',
      label: t('studentName'),
      sortable: true,
      width: '35%'
    },
    {
      id: 'email',
      label: t('studentEmail'),
      sortable: true,
      width: '40%'
    },
    {
      id: 'actions',
      label: !isAddingStudent ? (
        <Container sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '16px' }}>
          <Tooltip title={t('addStudent')}>
            <IconButton onClick={() => { setIsAddingStudent(true); scrollToAddStudentRow(); }} sx={{width: '34px', height: '34px'  }}>
              <img src={AddUserIcon} alt="Add User" height={26} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('importTable')}>
            <IconButton 
              onClick={() => setIsImportModalOpen(true)} 
              sx={{p: '5px', width: '34px', height: '34px' }}
            >
              <IconImportTable color="black" />
            </IconButton>
          </Tooltip>
        </Container>
      ) : '',
      sortable: false,
      width: '20%'
    }
  ];

  // Event Handlers
  const handleRequestSort = (property: keyof Student) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleStudentAccess = async (student: Student) => {
    try {
      setIsLoading(true);
      const studentData = await teacherStore.getStudentData(student.id ?? 0);
      navigate('/student-area', {
        state: {
          studentData,
          studentId: student.id,
          studentName: student.name,
          studentEmail: student.email
        }
      });
    } catch (error) {
      console.error('Error fetching student data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelAdd = () => {
    setIsAddingStudent(false);
    setNewStudent({ name: '', email: '' });
  };

  const handleAddStudent = async () => {
    if (newStudent.name && newStudent.email) {
        setIsSaving(true);
        try {
            const response: ImportClassResponse = await teacherStore.addSingleStudentToClass(classItem.id, newStudent);
            
            // טיפול בכשלונות
            if (response.details?.failed && response.details.failed.length > 0) {
                setFailedUsers(response.details.failed);
                setShowFailedUsersModal(true);
            }
            
            // עדכון מקומי של הטבלה רק אם היה הצלחה
            if (response?.teacherData?.schools && response.status !== 'error') {
                const updatedClass = response.teacherData.schools
                    .flatMap(school => school.grades)
                    .find(grade => grade.id === classItem.id);
                
                if (updatedClass) {
                    classItem.students = updatedClass.students;
                }
            }

            // סגירת שורת ההוספה רק אם לא היה error מלא
            if (response.status !== 'error') {
                setNewStudent({ name: '', email: '' });
                setIsAddingStudent(false);
            }
        } catch (error) {
            console.error("Error adding student:", error);
        } finally {
            setIsSaving(false);
        }
    }
  };

  const handleImportStudentsToClass = async (file: File) => {
    setIsLoading(true);
    try {
      const response = await teacherStore.addStudentsToClass(classItem.id, file);
      
      // טיפול בכשלונות
      if (response.details?.failed && response.details.failed.length > 0) {
        setFailedUsers(response.details.failed);
        setShowFailedUsersModal(true);
      }

      // עדכון מקומי של הטבלה
      if (response?.teacherData?.schools) {
        for (const school of response.teacherData.schools) {
          const updatedClass = school.grades.find((grade: Class) => grade.id === classItem.id);
          if (updatedClass) {
            classItem.students = updatedClass.students;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Error importing students:", error);
    } finally {
      setIsLoading(false);
      setIsImportModalOpen(false); // סגירת המודל אחרי הייבוא
    }
  };

  // Memoized Sorted Students
  const sortedStudents = useMemo(() => {
    const comparator = (a: Student, b: Student) => {
      const aValue = a[orderBy] as string;
      const bValue = b[orderBy] as string;
      
      if (order === 'desc') {
        return bValue.localeCompare(aValue);
      }
      return aValue.localeCompare(bValue);
    };

    return [...classItem.students].sort(comparator);
  }, [classItem.students, order, orderBy, teacherStore.currentSchool]);

  return (
    <>
      {isLoading && <LoaderComponent />}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 400,
          width: '100%',
          "& .MuiTableContainer-root": {
            overflow: "auto"
          }
        }}
      >
        <Table data-class-id={classItem.id} size="medium" stickyHeader >
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sx={{
                    backgroundColor: 'background.paper',
                    fontWeight: 'bold',
                    width: headCell.width,
                    minWidth: headCell.width,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id as keyof Student)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.id === 'actions' ? (
                      <Container sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '16px' }}>
                        <Tooltip title={t('addStudent')}>
                          <IconButton onClick={() => { setIsAddingStudent(true); scrollToAddStudentRow(); }} sx={{width: '34px', height: '34px'  }}>
                            <img src={AddUserIcon} alt="Add User" height={26} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('importTable')}>
                          <IconButton 
                            onClick={() => setIsImportModalOpen(true)} 
                            sx={{p: '5px', width: '34px', height: '34px' }}
                          >
                            <IconImportTable color="black" />
                          </IconButton>
                        </Tooltip>
                      </Container>
                    ) : headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStudents.map((student, index) => (
              <TableRow
                key={student.id}
                sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>{student.name}</TableCell>
                <TableCell>{student.email}</TableCell>
                <TableCell align="right">
                  <Tooltip title={t('studentAccess')}>
                    <IconButton
                      onClick={() => handleStudentAccess(student)}
                      onMouseEnter={() => setHoveredStudentId(student.id ?? 0)}
                      onMouseLeave={() => setHoveredStudentId(null)}
                    >
                      <div style={{
                        position: 'relative',
                        width: '26px',
                        height: '26px'
                      }}>
                        <img
                          src={CloseDoorIcon}
                          alt="door icon"
                          style={{
                            position: 'absolute',
                            right: '50%',
                            transform: 'translateX(50%)',
                            opacity: hoveredStudentId === student.id ? 0 : 1,
                            transition: 'opacity 0.3s ease-in-out'
                          }}
                          height={26}
                        />
                        <img
                          src={OpenDoorIcon}
                          alt="door icon"
                          style={{
                            position: 'absolute',
                            right: '50%',
                            transform: 'translateX(50%)',
                            opacity: hoveredStudentId === student.id ? 1 : 0,
                            transition: 'opacity 0.3s ease-in-out'
                          }}
                          height={26}
                        />
                      </div>
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {isAddingStudent && (
              <TableRow className="new-student-row" ref={addStudentRowRef}>
                <TableCell>{sortedStudents.length + 1}</TableCell>
                <TableCell>
                  <input
                    value={newStudent.name}
                    onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                    placeholder={t('enterName')}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}
                    disabled={isSaving}
                  />
                </TableCell>
                <TableCell>
                  <input
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                    placeholder={t('enterEmail')}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}
                    disabled={isSaving}
                  />
                </TableCell>
                <TableCell align="right">
                  {!isSaving && (
                    <>
                      <IconButton
                        onClick={handleAddStudent}
                        color="primary"
                        disabled={!newStudent.name || !newStudent.email}
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={handleCancelAdd}>
                        <CancelIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <FailedUsersModal
        isOpen={showFailedUsersModal}
        onClose={() => setShowFailedUsersModal(false)}
        failedUsers={failedUsers}
      />

      <ImportExcelModal
        isModalOpen={isImportModalOpen}
        setIsModalOpen={setIsImportModalOpen}
        handleImportStudents={handleImportStudentsToClass}
      />
    </>
  );
});

export default StudentsTable;
